/**
 * The bank's clock, as the bank last said it (DCTA-85, QA P1-2).
 *
 * Every time printed on a bank surface is the bank's: a step-up deadline is a promise
 * about when the BANK stops accepting the code, "Cancelled at" is when the bank
 * cancelled, "read at" is when the bank read. The phone's wall clock can be anything
 * (a skewed phone; judge mode's fast-forward moves only the bank's clock), so the
 * phone keeps just the distance between the bank's last word and its own clock at
 * that moment; a time asked later is the bank's time moved on by the phone's stopwatch.
 *
 * Until the bank has said a time, there is none: callers print no time rather than
 * the phone's (`now()` / `iso()` are `undefined`). `msOr()` falls back to the phone —
 * for grouping by day only ("Today"), never for a printed clock time.
 */
export class BankClock {
  readonly #phone: () => number;
  #offset: number | undefined;

  constructor(phone: () => number = Date.now) {
    this.#phone = phone;
  }

  /** The bank said this time (ISO 8601) just now. */
  learn(iso: string): void {
    const at = Date.parse(iso);
    if (Number.isNaN(at)) return;
    this.#offset = at - this.#phone();
  }

  now(): Date | undefined {
    return this.#offset === undefined ? undefined : new Date(this.#phone() + this.#offset);
  }

  iso(): string | undefined {
    return this.now()?.toISOString();
  }

  /** Milliseconds for day grouping: the bank's when known, the phone's until then. */
  msOr(): number {
    return this.#phone() + (this.#offset ?? 0);
  }
}

/**
 * The bank's "now" an answer carries, if it carries one: a top-level `now` on any answer
 * (`/healthz`, the demo clock) and `as_of` on the accounts reads (DCTA-77 ask bb) and the
 * gateway's answers — confirm, step-up, cancel, the receipt read (DCTA-86: the bank's clock
 * when it answered). A quote's or an FX rate's `as_of` is the market's
 * time, not the bank's now — never learned from.
 */
export function bankTimeOf(path: string, body: unknown): string | undefined {
  if (body === null || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  if (typeof o["now"] === "string") return o["now"];
  if (/^\/v1\/(accounts(\/[^/]+)?|gateway\/.+)$/.test(path) && typeof o["as_of"] === "string") return o["as_of"];
  return undefined;
}
